import React from 'react';
import {
    ApprovalInstance,
    LeaveRequestApprovalInstance,
} from '../../../api/ApprovalApi';
import {DetailRow, DetailSection} from './BaseApprovalDetailComponents';
import {BaseApprovalDetailModal} from './BaseApprovalDetailModal';
import {TeamAbsenceMiniPanel} from './TeamAbsenceMiniPanel';
import {useTranslation} from 'react-i18next';
import Typography from '../../../components/Typography';

export interface LeaveRequestApprovalDetailModalProps {
    instance: LeaveRequestApprovalInstance;
    open: boolean;
    onClose: () => void;
    onSuccess: (updated: ApprovalInstance) => void;
}

export function LeaveRequestApprovalDetailModal({
                                                    instance,
                                                    open,
                                                    onClose,
                                                    onSuccess,
                                                }: LeaveRequestApprovalDetailModalProps) {
    const {t, i18n} = useTranslation();
    const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
    const {approvableEntity} = instance;
    const employee = approvableEntity.employee;

    const formatDate = (iso: string | null | undefined) =>
        iso
            ? new Date(iso).toLocaleDateString(locale, {
                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
            })
            : '–';

    const leaveTypeName = approvableEntity.leaveType?.name
        ? t(`request.leaveRequest.leaveType.${approvableEntity.leaveType.name}`, {
            defaultValue: approvableEntity.leaveType.name,
        })
        : '–';

    const leftContent = (
        <>
            <DetailSection title={t('request.leaveRequest.detail.period')}>
                <DetailRow label={t('request.leaveRequest.leaveType.label')}>
                    {leaveTypeName}
                </DetailRow>
                <DetailRow label={t('request.leaveRequest.detail.startDate')}>
                    <span>
                        {formatDate(approvableEntity.leaveStart)}
                        {approvableEntity.startHalfDay && (
                            <span className="ml-1 text-xs text-gray-400">
                                ({t('request.leaveRequest.halfDay')})
                            </span>
                        )}
                    </span>
                </DetailRow>
                <DetailRow label={t('request.leaveRequest.detail.endDate')}>
                    <span>
                        {formatDate(approvableEntity.leaveEnd)}
                        {approvableEntity.endHalfDay && (
                            <span className="ml-1 text-xs text-gray-400">
                                ({t('request.leaveRequest.halfDay')})
                            </span>
                        )}
                    </span>
                </DetailRow>
                <DetailRow label={t('request.leaveRequest.days')}>
                    <span className="font-medium">{approvableEntity.leaveDays ?? '–'}</span>
                </DetailRow>
            </DetailSection>

            {approvableEntity.description && (
                <DetailSection title={t('request.detail.description')}>
                    <p className="py-2 text-sm text-gray-700 whitespace-pre-wrap">
                        {approvableEntity.description}
                    </p>
                </DetailSection>
            )}
        </>
    );

    // ── Right: team absence overview ─────────────────────────────────────────

    const rightContent = approvableEntity.leaveStart && approvableEntity.leaveEnd ? (
        <>
            <Typography variant="body2" color="secondary" className="mb-3 text-xs">
                Wer ist im selben Zeitraum abwesend?
            </Typography>
            <TeamAbsenceMiniPanel
                leaveStart={approvableEntity.leaveStart}
                leaveEnd={approvableEntity.leaveEnd}
                requestingEmployeeCode={employee?.employeeCode ?? ''}
            />
        </>
    ) : (
        <Typography variant="body2" color="secondary">
            Kein Zeitraum angegeben.
        </Typography>
    );

    return (
        <BaseApprovalDetailModal
            instance={instance}
            open={open}
            onClose={onClose}
            onSuccess={onSuccess}
            title={t('request.leaveRequest.detail.title', {defaultValue: 'Urlaubsantrag'})}
            employee={employee}
        >

        </BaseApprovalDetailModal>
    );
}